import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from './authService';

// Query Keys
export const favoriteVendorKeys = {
  all: ['favorite-vendors'],
  list: () => [...favoriteVendorKeys.all, 'list'],
  check: (vendorId) => [...favoriteVendorKeys.all, 'check', vendorId],
};

// API Functions
const favoriteVendorApi = {
  // Get all favorite vendors
  getFavorites: async () => {
    const response = await api.get('/user/favorite-vendors');
    return response.data;
  },

  // Check if vendor is favorite
  checkFavorite: async (vendorId) => {
    // Get all favorites and check if vendor is in the list
    const response = await api.get('/user/favorite-vendors');
    const favorites = response.data?.data?.data || response.data?.data || [];
    const favorite = favorites.find(fav => {
      const favVendorId = fav.vendor_id || fav.vendor?.id || fav.vendor_id;
      return favVendorId === vendorId;
    });
    return { 
      is_favorite: !!favorite, 
      favorite_id: favorite?.id || null 
    };
  },

  // Add vendor to favorites
  addFavorite: async (vendorId, notes = null) => {
    const response = await api.post('/user/favorite-vendors', {
      vendor_id: vendorId,
      notes: notes
    });
    return response.data;
  },

  // Remove vendor from favorites
  removeFavorite: async (favoriteId) => {
    const response = await api.delete(`/user/favorite-vendors/${favoriteId}`);
    return response.data;
  },
};

// React Query Hooks

// Get all favorite vendors
export const useFavoriteVendors = (options = {}) => {
  return useQuery({
    queryKey: favoriteVendorKeys.list(),
    queryFn: () => favoriteVendorApi.getFavorites(),
    ...options
  });
};

// Check if vendor is favorite
export const useCheckFavorite = (vendorId, options = {}) => {
  return useQuery({
    queryKey: favoriteVendorKeys.check(vendorId),
    queryFn: () => favoriteVendorApi.checkFavorite(vendorId),
    enabled: !!vendorId && (options.enabled !== false),
    // Don't refetch on window focus to keep optimistic updates
    refetchOnWindowFocus: false,
    // Keep stale time short so optimistic updates are visible
    staleTime: 0,
    ...options
  });
};

// Add vendor to favorites mutation
export const useAddFavorite = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ vendorId, notes }) => favoriteVendorApi.addFavorite(vendorId, notes),
    onMutate: async ({ vendorId }) => {
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      await queryClient.cancelQueries({ queryKey: favoriteVendorKeys.check(vendorId) });
      
      // Snapshot the previous value
      const previousData = queryClient.getQueryData(favoriteVendorKeys.check(vendorId));
      
      // Optimistically update the check query cache IMMEDIATELY
      queryClient.setQueryData(
        favoriteVendorKeys.check(vendorId),
        (oldData) => ({
          ...(oldData || {}),
          is_favorite: true,
          favorite_id: 'optimistic'
        })
      );
      
      // Return context with the previous value for rollback
      return { previousData, vendorId };
    },
    onSuccess: (data, variables, context) => {
      // Update with the actual favorite_id from the response (don't invalidate - keep optimistic update)
      queryClient.setQueryData(
        favoriteVendorKeys.check(variables.vendorId),
        (oldData) => ({
          ...(oldData || {}),
          is_favorite: true,
          favorite_id: data?.data?.id || data?.data?.data?.id || null
        })
      );
      // Invalidate and refetch favorites list (but not the check query to keep UI instant)
      queryClient.invalidateQueries({ queryKey: favoriteVendorKeys.list() });
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Revert optimistic update on error
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(
          favoriteVendorKeys.check(variables.vendorId),
          context.previousData
        );
      } else {
        queryClient.invalidateQueries({ queryKey: favoriteVendorKeys.check(variables.vendorId) });
      }
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options
  });
};

// Remove vendor from favorites mutation
export const useRemoveFavorite = (options = {}) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    onMutate: async ({ favoriteId, vendorId }) => {
      // CRITICAL: Optimistic update happens FIRST in onMutate (before mutationFn)
      // This ensures the UI updates immediately
      
      // Cancel any outgoing refetches to avoid overwriting our optimistic update
      if (vendorId) {
        await queryClient.cancelQueries({ queryKey: favoriteVendorKeys.check(vendorId) });
      }
      await queryClient.cancelQueries({ queryKey: favoriteVendorKeys.list() });
      
      // Snapshot the previous values
      const previousCheckData = vendorId ? queryClient.getQueryData(favoriteVendorKeys.check(vendorId)) : null;
      const previousListData = queryClient.getQueryData(favoriteVendorKeys.list());
      
      // Optimistically update the check query cache IMMEDIATELY
      // This makes the heart turn gray instantly - UI updates right away
      if (vendorId) {
        // Update cache with the same structure that the API returns
        queryClient.setQueryData(
          favoriteVendorKeys.check(vendorId),
          (oldData) => ({
            ...(oldData || {}),
            is_favorite: false,
            favorite_id: null
          })
        );
      }
      
      // Optimistically remove from favorites list IMMEDIATELY
      queryClient.setQueryData(
        favoriteVendorKeys.list(),
        (oldData) => {
          if (!oldData) return oldData;
          
          // Handle paginated response structure
          const data = oldData.data || oldData;
          const favoritesList = data?.data || data || [];
          
          // Remove the favorite from the list
          const updatedList = favoritesList.filter(fav => {
            const favId = fav.id;
            const favVendorId = fav.vendor_id || fav.vendor?.id;
            // Remove if it matches the favoriteId OR if favoriteId is 'optimistic' and vendorId matches
            return favId !== favoriteId && (favoriteId !== 'optimistic' || favVendorId !== vendorId);
          });
          
          // Return updated structure
          if (oldData.data) {
            return {
              ...oldData,
              data: {
                ...data,
                data: updatedList,
                total: (data.total || updatedList.length) - 1
              }
            };
          } else if (Array.isArray(oldData)) {
            return updatedList;
          } else {
            return {
              ...oldData,
              data: updatedList
            };
          }
        }
      );
      
      // If favoriteId is 'optimistic', we need to find the real one for the API call
      let actualFavoriteId = favoriteId;
      if (!actualFavoriteId || actualFavoriteId === 'optimistic') {
        // Try to get from cache first
        const favorites = previousListData;
        let favoritesList = favorites?.data?.data || favorites?.data || [];
        
        // If not in cache, we'll fetch in mutationFn
        if (favoritesList && favoritesList.length > 0) {
          const favorite = favoritesList.find(fav => {
            const favVendorId = fav.vendor_id || fav.vendor?.id;
            return favVendorId === vendorId;
          });
          actualFavoriteId = favorite?.id || null;
        }
      }
      
      return { favoriteId: actualFavoriteId, vendorId, previousCheckData, previousListData };
    },
    mutationFn: async ({ favoriteId, vendorId }) => {
      // Resolve the actual favoriteId if needed
      let actualFavoriteId = favoriteId;
      
      // If 'optimistic' or invalid, fetch from API
      if (!actualFavoriteId || actualFavoriteId === 'optimistic') {
        const favoritesResponse = await favoriteVendorApi.getFavorites();
        const favoritesList = favoritesResponse?.data?.data || favoritesResponse?.data || [];
        const favorite = favoritesList.find(fav => {
          const favVendorId = fav.vendor_id || fav.vendor?.id;
          return favVendorId === vendorId;
        });
        
        if (!favorite?.id) {
          throw new Error('Favorite not found');
        }
        
        actualFavoriteId = favorite.id;
      }
      
      return favoriteVendorApi.removeFavorite(actualFavoriteId);
    },
    onSuccess: (data, variables, context) => {
      // Update is already done optimistically, just ensure consistency
      if (context?.vendorId) {
        // Don't invalidate - just ensure the cache is correct
        queryClient.setQueryData(
          favoriteVendorKeys.check(context.vendorId),
          (oldData) => ({
            ...(oldData || {}),
            is_favorite: false,
            favorite_id: null
          })
        );
      }
      // Don't invalidate - optimistic update already handled it
      // Only refetch if needed for consistency (but optimistic update should be enough)
      // queryClient.invalidateQueries({ queryKey: favoriteVendorKeys.list() });
      // Call custom onSuccess if provided
      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      // Revert optimistic update on error
      if (context?.vendorId && context?.previousCheckData !== undefined) {
        queryClient.setQueryData(
          favoriteVendorKeys.check(context.vendorId),
          context.previousCheckData
        );
      } else if (context?.vendorId) {
        queryClient.invalidateQueries({ queryKey: favoriteVendorKeys.check(context.vendorId) });
      }
      
      // Revert favorites list optimistic update
      if (context?.previousListData !== undefined) {
        queryClient.setQueryData(
          favoriteVendorKeys.list(),
          context.previousListData
        );
      } else {
        queryClient.invalidateQueries({ queryKey: favoriteVendorKeys.list() });
      }
      
      // Call custom onError if provided
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
    ...options
  });
};

